import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ExternalLink,
  ChevronDown,
  FileText,
  Scale,
  AlertCircle,
  BookOpen,
  Download,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";
import html2pdf from "html2pdf.js";
import type {
  LegalReportProps,
  ChecklistItem,
  SourceItem,
  ClaimItem,
  ContradictionItem,
  ExplanationDetails,
} from "../types";

// Helper to get verdict color and icon
const getVerdictStyle = (verdict: string) => {
  const v = verdict?.toLowerCase() || "";
  if (v.includes("legal") || v.includes("allowed") || v.includes("permitted")) {
    return {
      color: "text-[#10B981]",
      bg: "bg-[#10B981]",
      border: "border-[#10B981]",
      icon: CheckCircle2,
      gradient: "from-[#10B981] to-[#059669]",
    };
  }
  if (
    v.includes("illegal") ||
    v.includes("prohibited") ||
    v.includes("forbidden")
  ) {
    return {
      color: "text-[#EF4444]",
      bg: "bg-[#EF4444]",
      border: "border-[#EF4444]",
      icon: XCircle,
      gradient: "from-[#EF4444] to-[#DC2626]",
    };
  }
  return {
    color: "text-[#F59E0B]",
    bg: "bg-[#F59E0B]",
    border: "border-[#F59E0B]",
    icon: AlertTriangle,
    gradient: "from-[#F59E0B] to-[#D97706]",
  };
};

// Helper to get checklist status style
const getChecklistStatusStyle = (status: string) => {
  switch (status) {
    case "Required":
      return {
        color: "text-[#EF4444]",
        bg: "bg-[#EF4444]",
        dot: "bg-[#EF4444]",
      };
    case "Recommended":
      return {
        color: "text-[#F59E0B]",
        bg: "bg-[#F59E0B]",
        dot: "bg-[#F59E0B]",
      };
    default:
      return {
        color: "text-[#64748B]",
        bg: "bg-[#64748B]",
        dot: "bg-[#64748B]",
      };
  }
};

// Collapsible Section Component - redesigned
const CollapsibleSection = ({
  title,
  children,
  defaultOpen = false,
  badge,
  icon: Icon,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badge?: React.ReactNode;
  icon?: LucideIcon;
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="group">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-3 border-b border-[#2D2D4A]/50 hover:border-[#7C3AED]/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          {Icon && (
            <Icon className="h-4 w-4 text-[#64748B]" strokeWidth={1.5} />
          )}
          <span className="text-sm font-medium text-[#94A3B8] group-hover:text-[#E2E8F0] transition-colors">
            {title}
          </span>
          {badge}
        </div>
        <div
          className={`transform transition-transform ${isOpen ? "rotate-180" : ""}`}
        >
          <ChevronDown className="h-4 w-4 text-[#64748B]" />
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-[2000px] opacity-100 pt-4" : "max-h-0 opacity-0"}`}
      >
        {children}
      </div>
    </div>
  );
};

// Source Item Component - minimal card
const SourceCard = ({ source }: { source: SourceItem }) => (
  <a
    href={source.url}
    target="_blank"
    rel="noopener noreferrer"
    className="block p-3 rounded-lg bg-[#0A0A12] border border-[#2D2D4A] hover:border-[#7C3AED]/40 transition-all group"
  >
    <div className="flex items-start gap-2 mb-2">
      <span
        className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider ${
          source.source_type === "official"
            ? "bg-[#10B981]/10 text-[#10B981]"
            : "bg-[#3D3D5C] text-[#64748B]"
        }`}
      >
        {source.source_type}
      </span>
    </div>
    <p className="text-sm text-[#E2E8F0] group-hover:text-[#A78BFA] transition-colors line-clamp-1 flex items-center gap-1">
      {source.title}
      <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
    </p>
    <p className="text-xs text-[#64748B] mt-1">{source.domain}</p>
  </a>
);

// Claim Item Component - tag style
const ClaimCard = ({ claim }: { claim: ClaimItem }) => (
  <div className="flex items-start gap-3 py-3 border-b border-[#2D2D4A]/30 last:border-0">
    <div className="w-1 h-full min-h-[40px] rounded-full bg-gradient-to-b from-[#7C3AED] to-[#7C3AED]/20 flex-shrink-0"></div>
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#7C3AED]/10 text-[#A78BFA] uppercase tracking-wider">
          {claim.category}
        </span>
        <span className="text-[10px] text-[#64748B]">
          {(claim.confidence * 100).toFixed(0)}% confident
        </span>
      </div>
      <p className="text-sm text-[#E2E8F0]">{claim.claim}</p>
      {claim.conditions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {claim.conditions.map((c, i) => (
            <span
              key={i}
              className="text-xs px-2 py-0.5 rounded bg-[#1A1A2E] text-[#94A3B8]"
            >
              {c}
            </span>
          ))}
        </div>
      )}
    </div>
  </div>
);

// Contradiction Item Component - warning style
const ContradictionCard = ({
  contradiction,
}: {
  contradiction: ContradictionItem;
}) => (
  <div className="relative pl-4 py-3 border-l-2 border-[#F59E0B]">
    <div className="flex items-center gap-2 mb-2">
      <span
        className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${
          contradiction.severity === "high"
            ? "bg-[#EF4444]/10 text-[#EF4444]"
            : contradiction.severity === "medium"
              ? "bg-[#F59E0B]/10 text-[#F59E0B]"
              : "bg-[#FBBF24]/10 text-[#FBBF24]"
        }`}
      >
        {contradiction.severity}
      </span>
      <span className="text-[10px] text-[#64748B]">
        {contradiction.contradiction_type}
      </span>
    </div>
    <p className="text-sm text-[#E2E8F0]">{contradiction.explanation}</p>
    {contradiction.resolution_notes && (
      <p className="text-xs text-[#64748B] mt-2">
        → {contradiction.resolution_notes}
      </p>
    )}
  </div>
);

// Checklist Item Component - list style
const ChecklistCard = ({ item }: { item: ChecklistItem }) => {
  const style = getChecklistStatusStyle(item.status);
  return (
    <div className="flex items-start gap-3 py-3 border-b border-[#2D2D4A]/30 last:border-0">
      <div
        className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${style.dot}`}
      ></div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-xs font-medium ${style.color}`}>
            {item.status}
          </span>
          <span className="text-[10px] text-[#3D3D5C]">•</span>
          <span className="text-sm text-[#E2E8F0]">{item.item}</span>
        </div>
        <p className="text-xs text-[#64748B] leading-relaxed">
          {item.description}
        </p>
        {item.citations.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {item.citations.map((citation, i) => (
              <a
                key={i}
                href={citation.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] text-[#7C3AED] hover:text-[#A78BFA] flex items-center gap-0.5 underline underline-offset-2"
              >
                {citation.title}
                <ExternalLink className="h-2 w-2" />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const LegalReport = ({
  report,
  isResetting,
  fadeInAnimation,
}: LegalReportProps) => {
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  if (!report) return null;

  const verdictStyle = getVerdictStyle(report.verdict);
  const VerdictIcon = verdictStyle.icon;

  // Parse explanation if it's a string
  const explanation: ExplanationDetails | null =
    typeof report.explanation === "string"
      ? null
      : (report.explanation as ExplanationDetails);

  const handleExportPdf = async () => {
    if (isGeneratingPdf) return;

    setIsGeneratingPdf(true);

    try {
      // Build plain text content for PDF
      const explanationText = explanation
        ? explanation.explanation
        : String(report.explanation);

      const keyFactors = explanation?.reasoning_details?.key_factors?.length
        ? `\nKey Factors: ${explanation.reasoning_details.key_factors.join(", ")}`
        : "";

      const confidence = explanation?.confidence
        ? `\nConfidence: ${(explanation.confidence * 100).toFixed(0)}%`
        : "";

      // Build checklist section
      let checklistSection = "";
      if (report.checklist.length > 0) {
        checklistSection = `
COMPLIANCE CHECKLIST
${"─".repeat(50)}
`;
        report.checklist.forEach((item, i) => {
          checklistSection += `
${i + 1}. [${item.status.toUpperCase()}] ${item.item}
   ${item.description}
`;
        });
      }

      // Build sources section
      let sourcesSection = "";
      if (report.sources.length > 0) {
        sourcesSection = `
SOURCES (${report.sources.length})
${"─".repeat(50)}
`;
        report.sources.forEach((source, i) => {
          sourcesSection += `
${i + 1}. ${source.title}
   Type: ${source.source_type} | Relevance: ${(source.relevance_score * 100).toFixed(0)}%
   URL: ${source.url}
`;
        });
      }

      // Build claims section
      let claimsSection = "";
      if (report.claims.length > 0) {
        claimsSection = `
LEGAL CLAIMS (${report.claims.length})
${"─".repeat(50)}
`;
        report.claims.forEach((claim, i) => {
          claimsSection += `
${i + 1}. [${claim.category.toUpperCase()}] Confidence: ${(claim.confidence * 100).toFixed(0)}%
   ${claim.claim}
   Source: ${claim.source_title}
`;
        });
      }

      // Build contradictions section
      let contradictionsSection = "";
      if (report.contradictions.length > 0) {
        contradictionsSection = `
CONTRADICTIONS (${report.contradictions.length})
${"─".repeat(50)}
`;
        report.contradictions.forEach((c, i) => {
          contradictionsSection += `
${i + 1}. [${c.severity.toUpperCase()}] ${c.contradiction_type || "Conflict"}
   ${c.explanation}
`;
        });
      }

      // Create clean text document
      const textContent = `
LEGAL ANALYSIS REPORT
${"═".repeat(50)}

Query: "${report.query}"
Jurisdiction: ${report.country}${report.subdivision ? `, ${report.subdivision}` : ""}
Generated: ${new Date(report.completed_at).toLocaleString()}

${"═".repeat(50)}
VERDICT: ${report.verdict.toUpperCase()}
${"═".repeat(50)}

${explanationText}
${keyFactors}${confidence}

${checklistSection}
${sourcesSection}
${claimsSection}
${contradictionsSection}

${"─".repeat(50)}
DISCLAIMER: This is AI-generated analysis and does not constitute legal advice.
Please consult a qualified legal professional for specific legal matters.
${"─".repeat(50)}
`;

      // Create a temporary element with clean styling
      const pdfContainer = document.createElement("div");
      pdfContainer.style.cssText = `
        font-family: 'Courier New', Courier, monospace;
        font-size: 11px;
        line-height: 1.6;
        color: #000;
        background: #fff;
        padding: 20px;
        white-space: pre-wrap;
        word-wrap: break-word;
      `;
      pdfContainer.textContent = textContent;
      document.body.appendChild(pdfContainer);

      const opt = {
        margin: [15, 15, 15, 15] as [number, number, number, number],
        filename: `legal-analysis-${report.country?.toLowerCase().replace(/\s+/g, "-") || "report"}-${new Date().toISOString().split("T")[0]}.pdf`,
        image: { type: "jpeg" as const, quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
        },
        jsPDF: {
          unit: "mm" as const,
          format: "a4" as const,
          orientation: "portrait" as const,
        },
      };

      await html2pdf().set(opt).from(pdfContainer).save();

      // Clean up
      document.body.removeChild(pdfContainer);
    } catch (error) {
      console.error("Failed to generate PDF:", error);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const requiredItems = report.checklist.filter(
    (item) => item.status === "Required",
  );
  const recommendedItems = report.checklist.filter(
    (item) => item.status === "Recommended",
  );

  return (
    <div
      className={`${fadeInAnimation.fadeIn} ${isResetting ? "opacity-0 transform -translate-y-4" : "opacity-100 transform translate-y-0"}`}
    >
      {/* Export PDF Button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={handleExportPdf}
          disabled={isGeneratingPdf}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#7C3AED] text-white hover:bg-[#6D28D9] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
        >
          {isGeneratingPdf ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Generating PDF...</span>
            </>
          ) : (
            <>
              <Download className="h-4 w-4" />
              <span>Export PDF</span>
            </>
          )}
        </button>
      </div>

      {/* Report Content */}
      <div>
        {/* Verdict Hero Card */}
        <div className="relative overflow-hidden rounded-2xl mb-6">
          {/* Gradient background */}
          <div
            className={`absolute inset-0 bg-gradient-to-br ${verdictStyle.gradient} opacity-10`}
          ></div>
          <div className="absolute inset-0 bg-[#0A0A12]/80"></div>

          {/* Content */}
          <div className="relative p-6">
            {/* Query context - top left */}
            <div className="mb-6">
              <p className="text-xs uppercase tracking-wider text-[#64748B] mb-1">
                Analysis for
              </p>
              <p className="text-[#E2E8F0] text-lg leading-relaxed">
                "{report.query}"
              </p>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-[#64748B]">
                  📍 {report.country}
                  {report.subdivision ? `, ${report.subdivision}` : ""}
                </span>
              </div>
            </div>

            {/* Verdict - prominent display */}
            <div className="flex items-start gap-4">
              <div
                className={`w-16 h-16 rounded-2xl ${verdictStyle.bg}/20 flex items-center justify-center border-2 ${verdictStyle.border}/30`}
              >
                <VerdictIcon className={`h-8 w-8 ${verdictStyle.color}`} />
              </div>
              <div className="flex-1">
                <h3 className={`text-3xl font-bold ${verdictStyle.color} mb-2`}>
                  {report.verdict}
                </h3>

                {explanation ? (
                  <div className="space-y-3">
                    <p className="text-[#94A3B8] leading-relaxed">
                      {explanation.explanation}
                    </p>

                    {explanation.reasoning_details?.key_factors.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {explanation.reasoning_details.key_factors.map(
                          (factor, i) => (
                            <span
                              key={i}
                              className="text-xs px-2 py-1 rounded-md bg-[#1A1A2E] text-[#94A3B8] border border-[#2D2D4A]"
                            >
                              {factor}
                            </span>
                          ),
                        )}
                      </div>
                    )}

                    {explanation.confidence && (
                      <div className="flex items-center gap-3 mt-4 pt-4 border-t border-[#2D2D4A]">
                        <span className="text-xs text-[#64748B] uppercase tracking-wider">
                          Confidence
                        </span>
                        <div className="flex-1 max-w-[200px] h-1.5 bg-[#1A1A2E] rounded-full overflow-hidden">
                          <div
                            className={`h-full ${verdictStyle.bg}`}
                            style={{
                              width: `${explanation.confidence * 100}%`,
                            }}
                          />
                        </div>
                        <span
                          className={`text-sm font-medium ${verdictStyle.color}`}
                        >
                          {(explanation.confidence * 100).toFixed(0)}%
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-[#94A3B8]">{String(report.explanation)}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Details sections */}
        <div className="bg-[#0A0A12] border border-[#2D2D4A] rounded-xl p-5 space-y-2">
          {/* Compliance Checklist */}
          {report.checklist.length > 0 && (
            <CollapsibleSection
              title="Compliance Checklist"
              defaultOpen={true}
              icon={FileText}
              badge={
                <div className="flex gap-2">
                  {requiredItems.length > 0 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#EF4444]/10 text-[#EF4444]">
                      {requiredItems.length} required
                    </span>
                  )}
                  {recommendedItems.length > 0 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F59E0B]/10 text-[#F59E0B]">
                      {recommendedItems.length} recommended
                    </span>
                  )}
                </div>
              }
            >
              <div>
                {report.checklist.map((item, index) => (
                  <ChecklistCard key={index} item={item} />
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* Sources */}
          {report.sources.length > 0 && (
            <CollapsibleSection
              title="Sources"
              icon={BookOpen}
              badge={
                <span className="text-[10px] text-[#64748B] ml-2">
                  {report.sources.length}
                </span>
              }
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {report.sources.map((source) => (
                  <SourceCard key={source.source_id} source={source} />
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* Claims */}
          {report.claims.length > 0 && (
            <CollapsibleSection
              title="Legal Claims"
              icon={Scale}
              badge={
                <span className="text-[10px] text-[#64748B] ml-2">
                  {report.claims.length}
                </span>
              }
            >
              <div>
                {report.claims.map((claim) => (
                  <ClaimCard key={claim.id} claim={claim} />
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* Contradictions */}
          {report.contradictions.length > 0 && (
            <CollapsibleSection
              title="Contradictions"
              icon={AlertCircle}
              badge={
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F59E0B]/10 text-[#F59E0B] ml-2">
                  {report.contradictions.length} found
                </span>
              }
            >
              <div className="space-y-3">
                {report.contradictions.map((contradiction, index) => (
                  <ContradictionCard
                    key={index}
                    contradiction={contradiction}
                  />
                ))}
              </div>
            </CollapsibleSection>
          )}
        </div>

        {/* Footer */}
        <div className="mt-4 flex items-center justify-between text-[10px] text-[#3D3D5C]">
          <span>{new Date(report.completed_at).toLocaleString()}</span>
          <span>AI-generated · Not legal advice</span>
        </div>
      </div>
    </div>
  );
};

export default LegalReport;
